import * as React from 'react';
import { FormControl, Button, Modal, Tooltip, Grid } from '@material-ui/core';
import { CommonStyles } from '../../hooks/styles';
import dataList from '../../constants/dataList';
import MUIDataTable from "mui-datatables";
import Loading from '../../components/loading';
import EditIcon from '@material-ui/icons/Edit';
import { DeleteForever } from '@material-ui/icons';
import PackingItemFormModal from './packing-items-form-modal.component';
import DeleteModal from '../../components/deleteModal';
import SearchFilter from '../../components/searchFilter';

interface PackingItemsProps {
	authUser?: any
	packingItems: any[]
	totalPackingItems: any
	globalRowsPerPage: any
	getPackingItems: (filter: any) => void
	deletePackingItem: (payload: any) => void
	setGlobalRowsPerPage: (numRows: any) => void
}

export const PackingItemsComponent: React.FC<PackingItemsProps> = (props) => {
	const classes = CommonStyles();
	const firstRender = React.useRef(false);
	const [modalOpen, setModalOpen] = React.useState(false)
	const [deleteModalOpen, setDeleteModalOpen] = React.useState(false)
	const [deleteId, setDeleteId] = React.useState(null as any)
	const [packingItemToEdit, setPackingItemToEdit] = React.useState(null as any)
	const [sortOrder, setSortOrder] = React.useState(null as any)
	const [tableStatePersist, setTableStatePersist] = React.useState(null as any);
	const [filters, setFilters] = React.useState<any>({})
	const [pagination, setPageState] = React.useState({
		offset: 0,
		limit: props.globalRowsPerPage,
		page: 0
	});

	const renderIcons: any = (tableMeta: any) => {
		return (
			<div style={{ color: 'rgba(0, 0, 0, 0.54)', float: 'right' }}>
				<Tooltip title="Edit" placement="bottom-start">
					<EditIcon
						onClick={(e) => {
							triggerModal(props.packingItems[tableMeta.rowIndex]);
						}}
						style={{ marginRight: 10, cursor: 'pointer' }}
					/>
				</Tooltip>
				<Tooltip title="Delete" placement="bottom-start">
					<DeleteForever
						onClick={(e) => {
							triggerDeleteModal(props.packingItems[tableMeta.rowIndex].id)
						}}
						style={{ cursor: 'pointer' }}
					/>
				</Tooltip>
			</div>
		)
	}

	const columnData = [
		{
			label: "ID",
			name: "id",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Description",
			name: "description",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Unit of Measure",
			name: "uom",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Priority",
			name: "priority",
			change: true,
			viewColumns: true,
			display: true
		},
		{
			label: "Width",
			name: "width",
			change: true,
			viewColumns: true,
			display: true,
			customBodyRender: (value: any, tableMeta: any) => {
				return <>{value ?? 0}mm</>
			}
		},
		{
			label: "Height",
			name: "height",
			change: true,
			viewColumns: true,
			display: true,
			customBodyRender: (value: any, tableMeta: any) => {
				return <>{value ?? 0}mm</>
			}
		},
		{
			label: "Length",
			name: "length",
			change: true,
			viewColumns: true,
			display: true,
			customBodyRender: (value: any, tableMeta: any) => {
				return <>{value ?? 0}mm</>
			}
		},
		{
			label: "Weight",
			name: "weight",
			change: true,
			viewColumns: true,
			display: true,
			customBodyRender: (value: any, tableMeta: any) => {
				return <>{value ?? 0}g</>
			}
		},
		{
			label: "Process",
			name: "",
			change: false,
			viewColumns: false,
			display: true,
			sort: false,
			customBodyRender: (value: any, tableMeta: any) => { 
				const item = props.packingItems[tableMeta.rowIndex]
				if(item.bagout && !item.packout && !item.supplies) return "Bagout"
				if(!item.bagout && item.packout && !item.supplies && !item.innerBox) return "Packout"
				if(!item.bagout && item.packout && item.supplies && !item.innerBox) return "Supply"
				if(!item.bagout && item.packout && item.innerBox) return "Inner Box"
				return "Unknown"
			}
		},
		{
			label: "",
			name: "",
			change: false,
			viewColumns: false,
			display: true,
			sort: false,
			customBodyRender: (value: any, tableMeta: any) => { return renderIcons(tableMeta) }
		},
	]

	const getTableColumns = () => {
		return columnData.map((column: any, index: number) => {
			return {
				label: column.label,
				name: column.name,
				options: {
					filter: true,
					display: tableStatePersist ? tableStatePersist.columns[index].display : column.display,
					change: column.change,
					sort: column.sort ?? true,
					viewColumns: column.viewColumns,
					...(column.customBodyRender) && { customBodyRender: column.customBodyRender },
				}
			}
		})
	}

	React.useEffect(() => {
		if (props.authUser && !firstRender.current) {
			filterPackingItems()
		}
		else {
			firstRender.current = false;
		}
	}, [pagination, sortOrder])

	const getFilter = (reset?: boolean) => {
		const filter: any = {
			limit: props.globalRowsPerPage,
			offset: pagination.offset,
			order: sortOrder ? [sortOrder] : ['id'],
			where: {} //filters ultimately end up here 
		}

		Object.keys(filters).forEach((filterKey: string) => {
			if (filterKey === 'description') {
				filter.where[filterKey] = filters[filterKey] ? { ilike: `%${filters[filterKey]}%` } : undefined
				return
			}
			filter.where[filterKey] = filters[filterKey] || undefined
		})

		return filter
	}

	const filterPackingItems = (reset?: boolean) => {
		props.getPackingItems({ filter: getFilter(reset) })
	}

	const handleSearch = () => {
		setPageState({
			offset: 0,
			limit: props.globalRowsPerPage,
			page: 0
		})
	}

	const showLoadingMsg = () => (
		!props.authUser && props.packingItems.length === 0
	)

	const handleModalClose = () => {
		setModalOpen(false);
		setPackingItemToEdit(null)
	};

	const handleDeleteModalClose = () => {
		setDeleteModalOpen(false);
		setDeleteId(null)
	};

	const triggerModal = (packItem: any) => {
		setPackingItemToEdit(packItem);
		setModalOpen(true);
	}

	const triggerDeleteModal = (id: any) => {
		setDeleteId(id);
		setDeleteModalOpen(true);
	}

	return (<div>
		{showLoadingMsg() ? <div><Loading message="" /></div> : <div>
			<div className={classes.searchWrap}>
				<Grid container>
					<Grid item xs={'auto'}>
						<SearchFilter
							config={[
								{
									label: 'Packing Item',
									name: 'description',
									type: 'text'
								}
							]}
							onFilterChange={setFilters}
							filters={filters}
							handleSearch={() => setPageState({...pagination, offset: 0, page: 0})}
						/>
					</Grid>
				</Grid>
			</div>

			<div className={classes.pageActionsWrap}>
				<FormControl className={classes.formControlSmall}>
					<Button
						className={classes.viewAllBtn}
						variant="contained"
						color="secondary"
						onClick={() => {
							triggerModal(null)
						}}
					> Add Packing Item </Button>
				</FormControl>
			</div>
			<div>
				<MUIDataTable
					data={props.packingItems}
					columns={getTableColumns()}
					options={{
						rowsPerPage: props.globalRowsPerPage,
						rowsPerPageOptions: dataList.PageLimitOptions,
						onChangeRowsPerPage: (numberOfRows: number) => {
							setPageState({ offset: 0, limit: numberOfRows, page: 0 });
							props.setGlobalRowsPerPage(numberOfRows);
						},
						filterType: "dropdown",
						responsive: "simple",
						filter: false,
						search: false,
						download: true,
						print: true,
						selectableRows: 'none',
						serverSide: true,
						page: pagination.page,
						count: props.totalPackingItems,
						onDownload: (buildHead: any, buildBody: any, columns: any) => {
							const filter = getFilter()
							delete filter.limit
							delete filter.offset
							const exportOptions = {
								columns: columns,
								timestampFormat: 'YYYY-MM-DD hh:ss a',
								fileName: 'packing-items'
							}
							props.getPackingItems({ filter: filter, exportOptions: exportOptions })
							return false
						},
						onColumnSortChange: (changedColumn: string, direction: string) => {
							setSortOrder(`${changedColumn} ${direction.toUpperCase()}`)
						},
						onTableChange: (tableAction: any, tableState: any) => {
							switch (tableAction) {
								case 'changePage':
									if (tableState.page > pagination.page) {
										setPageState({ offset: pagination.offset + props.globalRowsPerPage, limit: props.globalRowsPerPage, page: tableState.page });
									}
									else if (tableState.page < pagination.page) {
										setPageState({ offset: pagination.offset - props.globalRowsPerPage, limit: props.globalRowsPerPage, page: tableState.page });
									}
									break;
								case 'viewColumnsChange':
									setTableStatePersist(tableState);
									break;
							}
						},
					}}
				/>
			</div>
			{/* :
				<EmptyContent message="Change the filter criteria above to view product information." />
			} */}
			<Modal
				open={modalOpen}
				onClose={(e: any, reason: string) => {
					if (reason === 'backdropClick') return
					handleModalClose()
				}}
				aria-labelledby="simple-modal-title"
				aria-describedby="simple-modal-description"
			>
				<div>
					<PackingItemFormModal getPackingItems={filterPackingItems} packingItem={packingItemToEdit} closeModal={handleModalClose} />
				</div>
			</Modal>
			<Modal
				open={deleteModalOpen}
				onClose={handleDeleteModalClose}
				aria-labelledby="simple-modal-title"
				aria-describedby="simple-modal-description"
			>
				<div>
					<DeleteModal
						deleteId={deleteId}
						title={'Delete Packing Item'}
						message={`Are you sure you want to delete the packing item with ID ${deleteId}`}
						onDelete={props.deletePackingItem}
						onSuccess={handleSearch}
						closeModal={handleDeleteModalClose} />
				</div>
			</Modal>
		</div>}
	</div>)
}